import { useCallback } from "react";
import { z } from "zod";
import { ExerciseSchema, WorkoutSchema } from "../schemas/workout";
import { useWorkout } from "../contexts/WorkoutContext";

export type Exercise = z.infer<typeof ExerciseSchema>;
export type Workout = z.infer<typeof WorkoutSchema>;

export function useWorkoutEditorActions() {
  const { dispatch } = useWorkout();

  const handleWorkoutNameChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    dispatch({ type: "UPDATE_WORKOUT_NAME", payload: e.target.value });
  }, [dispatch]);

  const handleDescriptionChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    dispatch({ type: "UPDATE_WORKOUT_DESCRIPTION", payload: e.target.value });
  }, [dispatch]);

  const handleAddExercise = useCallback(() => {
    dispatch({ type: "ADD_EXERCISE" });
  }, [dispatch]);

  const handleExerciseChange = useCallback((updatedExercise: Exercise) => {
    dispatch({ type: "UPDATE_EXERCISE", payload: updatedExercise });
  }, [dispatch]);

  const handleExerciseDelete = useCallback((id: string) => {
    dispatch({ type: "DELETE_EXERCISE", payload: id });
  }, [dispatch]);

  const handleExerciseDuplicate = useCallback((id: string) => {
    dispatch({ type: "DUPLICATE_EXERCISE", payload: id });
  }, [dispatch]);

  const handleExerciseTypeChange = useCallback((exerciseId: string, type: Exercise["structure"]["type"]) => {
    dispatch({
      type: "UPDATE_EXERCISE_TYPE",
      payload: { exerciseId, type },
    });
  }, [dispatch]);

  const handleExerciseDetailsChange = useCallback((exerciseId: string, field: string, value: any) => {
    dispatch({
      type: "UPDATE_EXERCISE_DETAIL",
      payload: { exerciseId, field, value },
    });
  }, [dispatch]);

  const startEditing = useCallback((exerciseId: string, field: string) => {
    dispatch({
      type: "START_EDITING",
      payload: { exerciseId, field },
    });
  }, [dispatch]);

  const stopEditing = useCallback(() => {
    dispatch({ type: "STOP_EDITING" });
  }, [dispatch]);

  return {
    handleWorkoutNameChange,
    handleDescriptionChange,
    handleAddExercise,
    handleExerciseChange,
    handleExerciseDelete,
    handleExerciseDuplicate,
    handleExerciseTypeChange,
    handleExerciseDetailsChange,
    startEditing,
    stopEditing,
  };
}
